/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useEffect } from 'react';
import isEqual from 'lodash/isEqual';
import {
	useResource,
	createRouterContext,
	type RouterContext,
	type RouteResourceUpdater,
	createRouterSelector,
} from '@atlassian/react-resource-router';
import {
	getIdentifier,
	staticProjectSidebarNavigationResource,
} from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import type { ProjectSidebarNavInfo } from '@jira/navigation-apps__resources/src/services/sidebar/project-sidebar-navigation/types.tsx';

export const DUMMY_COMPONENT = (): null => null;
export type UseRouteResourceReturn = {
	data: ProjectSidebarNavInfo | null;
	update: (getNewData: RouteResourceUpdater<ProjectSidebarNavInfo | null>) => void;
};

export const createProjectRouteRouterContext = (
	projectKey: string | null,
): RouterContext | undefined =>
	projectKey
		? createRouterContext(
				{
					name: 'software',
					path: '/dummyroute/c/projects/:projectKey/dummyroute',
					component: DUMMY_COMPONENT,
				},
				{
					params: {
						projectKey,
					},
				},
			)
		: undefined;

export const useProjectRouteResource = (projectKey: string | null): UseRouteResourceReturn => {
	const { data, update } = useResource(staticProjectSidebarNavigationResource, {
		routerContext: createProjectRouteRouterContext(projectKey),
	});
	return {
		data,
		update,
	};
};

export const createBoardRouteRouterContext = (boardId: number | null): RouterContext | undefined =>
	boardId != null
		? createRouterContext(
				{
					name: 'software',
					path: '/dummyroute/c/projects/boards/:boardId/dummyroute',
					component: DUMMY_COMPONENT,
				},
				{
					params: {
						boardId: `${boardId}`,
					},
				},
			)
		: undefined;

export const useBoardRouteResource = (boardId: number | null): UseRouteResourceReturn => {
	const { data, update } = useResource(staticProjectSidebarNavigationResource, {
		routerContext: createBoardRouteRouterContext(boardId),
	});
	return {
		data,
		update,
	};
};

const useIsOnBoardBasedRoute = createRouterSelector(
	({ route, query, match }) =>
		getIdentifier(route.name, match, query)?.identifierType === 'boardId',
);

export const useJiraSoftwareCMPNavigationSynchronizer = (
	latestNavInfo: ProjectSidebarNavInfo | null,
): void => {
	const isOnBoardBasedRoute = useIsOnBoardBasedRoute();
	const isSoftwareCMP = latestNavInfo?.jiraSoftwareNavData?.project?.simplified === false;
	const pKey = (isSoftwareCMP && latestNavInfo?.jiraSoftwareNavData?.project?.key) || null;
	const boardId = (isSoftwareCMP && latestNavInfo?.jiraSoftwareNavData?.boardInfo?.id) || null;

	// explicitly get project data by forcing key to be project
	const { data: projectRouteNavInfo, update: updateProjectRoute } = useProjectRouteResource(pKey);
	// explicitly get board data by forcing key to be board
	const { data: boardRouteNavInfo, update: updateBoardRoute } = useBoardRouteResource(boardId);

	// if software CMP project or board data is not in sync, then force update
	useEffect(() => {
		if (latestNavInfo != null && isSoftwareCMP) {
			if (isOnBoardBasedRoute && !isEqual(latestNavInfo, projectRouteNavInfo)) {
				updateProjectRoute(() => latestNavInfo);
			} else if (!isEqual(latestNavInfo, boardRouteNavInfo)) {
				updateBoardRoute(() => latestNavInfo);
			}
		}
	}, [
		isOnBoardBasedRoute,
		latestNavInfo,
		updateProjectRoute,
		updateBoardRoute,
		projectRouteNavInfo,
		boardRouteNavInfo,
		isSoftwareCMP,
	]);
};
